// Prodejna nářadí (issue #37, screens/12-prodejna.md + m12-prodejna.md,
// mockupy design/„Prodejna nářadí.dc.html" a „Prodejna nářadí mobil.dc.html").
//
// Nativní stránka je `type-page`; obsah z administrace nese vlastní vrstvu
// předchozího správce (VČETNĚ inline `<style>` s `.mkn-*` a pravidlem
// `.in-prodejna-naradi header h1 { display: none }`):
//   section.mkn-section > .mkn-container.mkn-split
//     .mkn-card: span.mkn-eyebrow, h1.mkn-h1, p.mkn-lead, p.mkn-badge ×3,
//                p (sortiment), p (značky), a.mkn-btn (e-shop)
//     figure.mkn-media > img (fasáda prodejny)
//   section.mkn-section.mkn-reverse: figure + .mkn-card („E-shop i osobní odběr")
//   section.mkn-section.map: .mkn-map (adresa, otevírací doba, a.mkn-btn--ghost
//                            do map) + figure > video
//   h2 „Prodejna Jipos" + div.plus-gallery-wrap (11 fotek, nativní lightbox)
//   h2 „Mapa k prodejně" + p > iframe (Google Maps embed)
//
// ODCHYLKY OD SPECKY (dev instance 2026-07-30):
//   • NADPIS: nativní `header h1` schovává inline CSS z administrace (pravidlo
//     výše) — a schovávalo by ho i po přesunu do hera (selektor `header h1`
//     cestuje s uzlem). Titulek hera proto stavíme z `h1.mkn-h1` UVNITŘ obsahu,
//     nativní hlavičku needitujeme. Jediný viditelný H1 tak zůstane jeden.
//   • USP pás nestojí na kopii z návrhu, ale na REÁLNÝCH odznacích obsahu
//     („200 m² prodejní plochy", „možnost objednání online", „šíroký výběr" —
//     překlep je v administraci, nesahá se na něj) + osobní odběr zdarma.
//   • Dlaždice „Co u nás koupíte" se skládají z věty o sortimentu v obsahu
//     (ruční / elektrické / zahradní technika / příslušenství), popisky jsou
//     kopie z návrhu.
//   • Pás značek: obsah říká jen „od ověřených značek" bez výčtu → wordmarky
//     jsou ověřené proti `/znacka/` na dev instanci (2026-07-30) a odkazují
//     na reálné listingy značek.
//   • Telefon na stránce má PRÁZDNÝ odkaz `tel:+420 477 477 856`, který se
//     s projektovou konstantou `CONTACT.phone` (+420 417 823 111 z #5) ani
//     s čísly v zadání #37 neshoduje → tlačítko „Zavolat" neslo CONTACT.phone (dnes stránka
//     vlastní telefon nekreslí, import padl při lintu 2026-09-09)
//     a nesoulad je vedený v `.claude/night-notes.md` k rozhodnutí klienta.

import { icon } from './icon.js';
import { isOpenNow, openingHours } from './headerTools.js';
import L from '../locale/cs/storePage.js';
import SHOP from '../../config/shop.js';
import { page } from './pages.js';
import {
  el,
  link,
  textOf,
  iconBox,
  pageHost,
  pageIs,
  markReady,
  buildHero,
  buildBadge,
  buildHours,
  buildGallery,
  buildVideoBox,
  buildMapBox,
  sectionHead,
  centerHead,
} from './infoPage.js';


// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
const LABEL_ESHOP_CTA = L.eshopCta;
const LABEL_VISIT_CTA = L.visitCta;
const LABEL_OPEN_TODAY = L.openToday;
const LABEL_CLOSED_TODAY = L.closedToday;
const LABEL_CHOICE = L.choice;
const LABEL_CHOICE_PEREX = L.choicePerex;
const LABEL_ONLINE = L.online;
const LABEL_ONLINE_CTA = L.onlineCta;
const LABEL_PICKUP = L.pickup;
const LABEL_PICKUP_TEXT = L.pickupText;
const LABEL_ASSORT = L.assort;
const LABEL_ASSORT_HINT = L.assortHint;
const LABEL_BRANDS = L.brands;
const LABEL_MAP = L.map;
const LABEL_ADDRESS = L.address;
const LABEL_HOURS = L.hours;
const LABEL_MAP_CTA = L.mapCta;
const LABEL_MAP_PILL = L.mapPill;
const LABEL_VIDEO = L.video;
const LABEL_VIDEO_CAP = L.videoCap;
const LABEL_PICKUP_FREE = L.pickupFree;
const LABEL_SHIPPING_HREF = page('shipping');

// Sortiment: klíčové slovo z věty v obsahu → dlaždice (titulek, ikona, popis).
// Titulky i ikony jsou vázané na to, co obsah opravdu uvádí; popisky = kopie návrhu.
// `re` zůstává v kódu (S3 #399) — matchuje ADMIN OBSAH (věty o sortimentu),
// ne pevný text platformy.
const ASSORTMENT = [
  {
    re: /ru[čc]n[íi]/i,
    title: L.assortment[0].title,
    icon: 'wrench',
    desc: L.assortment[0].desc,
  },
  {
    re: /elektrick|aku/i,
    title: L.assortment[1].title,
    icon: 'bolt',
    desc: L.assortment[1].desc,
  },
  {
    re: /zahradn/i,
    title: L.assortment[2].title,
    icon: 'leaf',
    desc: L.assortment[2].desc,
  },
  {
    re: /p[řr][íi]slu[šs]enstv/i,
    title: L.assortment[3].title,
    icon: 'cog',
    desc: L.assortment[3].desc,
  },
];

// Wordmarky pásu značek = `brands.store` z manifestu shopu.
const BRANDS = SHOP.brands.store.map((b) => ({ label: b.label, href: page('brands', b.slug + '/') }));

const USP_ICONS = [
  [/m²|plochy|prodejn/i, 'store'],
  [/online|objedn/i, 'cart'],
  [/v[ýy]b[ěe]r|[šs][íi]rok/i, 'grid'],
  [/servis/i, 'wrench'],
];

function pickIcon(table, text, fallback) {
  for (let i = 0; i < table.length; i += 1) {
    if (table[i][0].test(text)) return table[i][1];
  }
  return fallback;
}

// „Po–Pá 07:30–17:00 • So 08:30–13:00" → řádky otevírací doby
function hoursFromText(text) {
  if (!text) return [];
  return text
    .split(/[•·]|\s{2,}/)
    .map(function (part) {
      return part.replace(/\s+/g, ' ').trim();
    })
    .filter(Boolean)
    .map(function (part) {
      const m = part.match(/^(.*?)\s*((?:\d{1,2}[:.]\d{2}\s*[–-]\s*\d{1,2}[:.]\d{2})|zav[řr]eno.*)$/i);
      if (!m) return { day: part, time: '' };
      return {
        day: m[1].trim(),
        time: m[2].replace(/\s*-\s*/, '–').trim(),
        closed: /zav[řr]eno/i.test(m[2]),
      };
    })
    .filter(function (row) {
      return row.day || row.time;
    });
}

function textRow(host, re) {
  const nodes = host ? host.querySelectorAll('p') : [];
  for (let i = 0; i < nodes.length; i += 1) {
    if (re.test(textOf(nodes[i]))) return nodes[i];
  }
  return null;
}

// Sortiment se v obsahu rozpadá do víc odstavců (perex + „Naše prodejna …
// nabízí ruční i elektrické nářadí, zahradní techniku a příslušenství") —
// dlaždice proto rozhodujeme podle SPOJENÉHO textu, ne podle prvního odstavce.
function joinedText(host) {
  return Array.prototype.map
    .call(host.querySelectorAll('p, li'), textOf)
    .filter(Boolean)
    .join(' ');
}

// ---------------------------------------------------------------------------
export function initStorePage() {
  if (!pageIs('store')) return;
  const host = pageHost();
  if (!host) return;
  if (document.querySelector('.bs-st')) return; // idempotence
  const about = host.about;

  const heroCard = about.querySelector('.mkn-card');
  const heroTitle = about.querySelector('h1.mkn-h1, .mkn-card h1');
  if (!heroCard || !heroTitle) return; // jiná struktura obsahu → nešaháme

  const eyebrow = textOf(about.querySelector('.mkn-eyebrow'));
  const lead = textOf(about.querySelector('.mkn-lead'));
  const badges = Array.prototype.map.call(about.querySelectorAll('.mkn-badge'), textOf);
  const eshopBtn = about.querySelector('a.mkn-btn:not(.mkn-btn--ghost)');
  const ghostBtn = about.querySelector('a.mkn-btn--ghost:not([href*="map"])');
  const mapBtn = about.querySelector('.mkn-map a[href*="map"], a.mkn-btn--ghost[href*="map"]');
  const mapPanel = about.querySelector('.mkn-map');
  const assortText = joinedText(about);
  const photos = about.querySelectorAll('figure.mkn-media img');
  const eshopHref = eshopBtn ? eshopBtn.getAttribute('href') : '/';

  const root = el('div', 'bs-ip bs-st');

  // --- 1) hero: text + fotka fasády s plovoucí kartou otevírací doby --------
  const address = textOf(textRow(mapPanel, /Teplick|Adresa/i)).replace(/^Adresa:?\s*/i, '');
  const hoursText = textOf(textRow(mapPanel, /Otev[íi]rac|Po[–-]P[áa]/i)).replace(
    /^Otev[íi]rac[íi] doba:?\s*/i,
    ''
  );
  const hours = hoursFromText(hoursText);
  const open = isOpenNow();
  // Plovoucí karta říká „Otevřeno dnes" → hodiny musí být DNEŠNÍ (o víkendu
  // 8:30–13:00, ne první řádek rozpisu Po–Pá); zdroj = sdílený CONTACT.openHours,
  // text z administrace je jen záloha pro den, který by v rozpisu chyběl.
  const todayRow = openingHours().find(function (row) {
    return row.today && row.open;
  });
  const todayHours = todayRow ? todayRow.value : hours.length ? hours[0].time : '';

  let side = null;
  if (photos.length) {
    side = el('div', 'bs-st__hero-media');
    const shot = photos[0];
    shot.classList.add('bs-st__hero-img');
    shot.loading = 'lazy';
    if (!shot.getAttribute('alt') || !shot.getAttribute('alt').trim()) {
      shot.setAttribute('alt', L.heroAlt);
    }
    side.appendChild(shot);
    side.appendChild(
      buildBadge({
        icon: 'store',
        variant: open ? 'open' : 'closed',
        title: open ? LABEL_OPEN_TODAY : LABEL_CLOSED_TODAY,
        text: [todayHours, address].filter(Boolean).join(' · '),
      })
    );
  }

  heroTitle.classList.add('bs-st__title');
  root.appendChild(
    buildHero(
      {
        eyebrow: eyebrow,
        title: heroTitle, // h1 z obsahu (nativní schovává inline CSS administrace)
        perex: lead,
        ctas: [
          { href: eshopHref, label: LABEL_ESHOP_CTA, icon: 'cart', variant: 'primary' },
          { href: '#mapa', label: LABEL_VISIT_CTA, icon: 'pin', variant: 'outline' },
        ],
      },
      side
    )
  );

  // --- 2) USP pás z reálných odznaků obsahu --------------------------------
  const usp = badges.slice();
  usp.push(LABEL_PICKUP_FREE);
  if (usp.length) {
    const band = el('section', 'bs-st__usp');
    usp.forEach(function (text) {
      const item = el('div', 'bs-st__usp-item');
      item.appendChild(iconBox(pickIcon(USP_ICONS, text, 'check'), 'bs-st__usp-ic'));
      item.appendChild(el('span', 'bs-st__usp-text', text));
      band.appendChild(item);
    });
    root.appendChild(band);
  }

  // --- 3) E-shop i osobní odběr -------------------------------------------
  const choice = el('section', 'bs-st__choice-sec');
  choice.appendChild(centerHead(LABEL_CHOICE, LABEL_CHOICE_PEREX));
  const choiceGrid = el('div', 'bs-st__choice');

  const onlineText = textOf(
    textRow(about.querySelectorAll('.mkn-card')[1] || about, /objednejte|online/i)
  );
  [
    {
      icon: 'cart',
      title: LABEL_ONLINE,
      text: onlineText,
      cta: { href: eshopHref, label: LABEL_ONLINE_CTA, variant: 'primary' },
    },
    {
      icon: 'store',
      title: LABEL_PICKUP,
      text: LABEL_PICKUP_TEXT,
      cta: ghostBtn
        ? {
            href: ghostBtn.getAttribute('href'),
            label: textOf(ghostBtn),
            variant: 'outline',
          }
        : { href: LABEL_SHIPPING_HREF, label: L.shippingLabel, variant: 'outline' },
    },
  ].forEach(function (def) {
    if (!def.text) return;
    const card = el('article', 'bs-st__choice-card');
    card.appendChild(iconBox(def.icon, 'bs-st__choice-ic'));
    card.appendChild(el('h3', 'bs-st__choice-title', def.title));
    card.appendChild(el('p', 'bs-st__choice-text', def.text));
    const cta = link(def.cta.href, 'bs-ip__cta bs-ip__cta--' + def.cta.variant + ' bs-st__choice-cta');
    cta.appendChild(el('span', null, def.cta.label));
    cta.appendChild(icon('arrow-right'));
    card.appendChild(cta);
    choiceGrid.appendChild(card);
  });
  if (choiceGrid.children.length) {
    choice.appendChild(choiceGrid);
    root.appendChild(choice);
  }

  // --- 4) Co u nás koupíte + pás značek -----------------------------------
  const tiles = ASSORTMENT.filter(function (def) {
    return !assortText || def.re.test(assortText);
  });
  if (tiles.length) {
    const sec = el('section', 'bs-st__assort-sec');
    sec.appendChild(sectionHead(LABEL_ASSORT, LABEL_ASSORT_HINT));
    const grid = el('div', 'bs-st__assort');
    tiles.forEach(function (def) {
      const tile = el('article', 'bs-st__tile');
      tile.appendChild(iconBox(def.icon, 'bs-st__tile-ic'));
      tile.appendChild(el('h3', 'bs-st__tile-title', def.title));
      tile.appendChild(el('p', 'bs-st__tile-desc', def.desc));
      grid.appendChild(tile);
    });
    sec.appendChild(grid);

    const strip = el('div', 'bs-st__brands');
    strip.appendChild(el('span', 'bs-st__brands-label', LABEL_BRANDS));
    const marks = el('div', 'bs-st__brands-list');
    BRANDS.forEach(function (brand) {
      const mark = link(brand.href, 'bs-st__wordmark');
      mark.textContent = brand.label;
      marks.appendChild(mark);
    });
    strip.appendChild(marks);
    sec.appendChild(strip);
    root.appendChild(sec);
  }

  // --- 5) #mapa Kde nás najdete -------------------------------------------
  const frame = about.querySelector('iframe');
  if (address || frame) {
    const sec = el('section', 'bs-st__map-sec');
    sec.id = 'mapa';

    const panel = el('aside', 'bs-st__panel');
    panel.appendChild(el('div', 'bs-st__panel-title', LABEL_MAP));
    if (address) {
      const row = el('div', 'bs-st__panel-row');
      row.appendChild(iconBox('pin', 'bs-st__panel-ic'));
      const body = el('div', 'bs-st__panel-body');
      body.appendChild(el('span', 'bs-st__panel-label', LABEL_ADDRESS));
      body.appendChild(el('span', 'bs-st__panel-value', address));
      row.appendChild(body);
      panel.appendChild(row);
    }
    if (hours.length) {
      const row = el('div', 'bs-st__panel-row');
      row.appendChild(iconBox('clock', 'bs-st__panel-ic'));
      const body = el('div', 'bs-st__panel-body');
      body.appendChild(el('span', 'bs-st__panel-label', LABEL_HOURS));
      body.appendChild(buildHours(hours, 'bs-st__panel-hours'));
      row.appendChild(body);
      panel.appendChild(row);
    }
    if (mapBtn) {
      const cta = link(mapBtn.getAttribute('href'), 'bs-ip__cta bs-ip__cta--primary bs-st__panel-cta');
      cta.target = '_blank';
      cta.rel = 'noopener noreferrer';
      cta.appendChild(icon('map'));
      cta.appendChild(el('span', null, textOf(mapBtn) || LABEL_MAP_CTA));
      panel.appendChild(cta);
    }
    sec.appendChild(panel);

    if (frame) {
      sec.appendChild(
        buildMapBox(frame, {
          title: LABEL_MAP,
          address: address,
          href: mapBtn ? mapBtn.getAttribute('href') : '',
          label: LABEL_MAP_PILL,
        })
      );
    }
    root.appendChild(sec);
  }

  // --- 6) videoprohlídka a galerie ----------------------------------------
  const video = about.querySelector('video');
  if (video) {
    root.appendChild(buildVideoBox(video, { title: LABEL_VIDEO, caption: LABEL_VIDEO_CAP }));
  }
  const anchors = Array.prototype.slice.call(
    about.querySelectorAll('.plus-gallery-item > a[href]')
  );
  if (anchors.length) {
    root.appendChild(
      buildGallery(anchors, { visible: 6, alt: L.galleryAlt })
    );
  }

  about.classList.add('bs-ip__native');
  about.insertAdjacentElement('beforebegin', root);
  markReady();
}
